import InputField from 'components/fields/InputField';
import LeadrsLogo from 'components/icons/LeadrsLogo';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { GoogleLogin } from '@react-oauth/google';
import { saveReferralCode, getReferralCode } from 'utils/referral';
import { MdVerified, MdPayments, MdInsights } from 'react-icons/md';

function SignUpSeller() {
    const [error, setError] = useState('');
    const [newUser, setNewUser] = useState({
        name: '',
        surname: '',
        email: '',
        password: '',
        siret: '00000000000000',
        phone: '',
        company: 'leadrs.co',
        referal: '',
        accept_tos: true,
        confirmPassword: '',
        type: 'seller',
        role: 'ceo',
        business: 'freelance',
        industry: 'Rénovation',
        channels: [],
        leadsPerWeek: 100,
    });

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref');
        if (ref) {
            saveReferralCode(ref);
            setNewUser((prev) => ({ ...prev, referal: ref }));
            return;
        }

        const storedRef = getReferralCode();
        if (storedRef) setNewUser((prev) => ({ ...prev, referal: storedRef }));
    }, []);

    const loginGoogle = (token: string) => {
        if (!newUser.phone.trim()) {
            setError('Le numéro de téléphone est obligatoire');
            return;
        }
        axios
            .post(`${CONFIG.apiUrl}/auth/google`, { ...newUser, token })
            .then((response: any) => {
                if (response.data.token) {
                    localStorage.setItem('token', response.data.token);
                    localStorage.setItem('user-type', response.data.type);
                    window.location.href = '/dashboard';
                }
            })
            .catch((error: any) => {
                console.log(error);
                setError('Erreur lors de la connexion avec Google');
            });
    };

    const handleChange = (e: any) => {
        const { id, value } = e.target;

        if (id === 'channels') {
            setNewUser((prev) => ({ ...prev, [id]: [value] }));
            return;
        }

        if (id === 'accept_tos') {
            setNewUser((prev) => ({ ...prev, [id]: !prev.accept_tos }));
            return;
        }

        setNewUser((prev) => ({ ...prev, [id]: value }));
        setError('');
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        if (newUser.password !== newUser.confirmPassword) return;
        if (!newUser.phone.trim()) {
            setError('Le numéro de téléphone est obligatoire');
            return;
        }
        const submitData = { ...newUser };
        delete submitData.confirmPassword;

        axios
            .post(`${CONFIG.apiUrl}/auth/register`, submitData)
            .then((res) => {
                localStorage.setItem('token', res.data.token);
                localStorage.setItem('user-type', newUser.type);
                if (res.data.token.length > 10) {
                    window.location.href = '/dashboard';
                }
            })
            .catch((err) => setError(err.response.data.error));
    };

    return (
        <div className="no-scrollbar relative grid h-screen w-full grid-cols-12">
            {/* Left — brand panel */}
            <div className="col-span-5 hidden h-full flex-col justify-between bg-leadrs-purple-soft p-12 lg:flex">
                <LeadrsLogo
                    size={36}
                    iconBg="#1C0F53"
                    iconRing="#EAE6FF"
                    textColor="#1C0F53"
                />

                <div>
                    <h1 className="font-crimson text-[52px] leading-[1.1] text-leadrs-purple-focus">
                        Vendez vos leads facilement.
                    </h1>
                    <p className="mt-6 max-w-sm font-geist text-base leading-relaxed text-leadrs-purple-focus/70">
                        Vendez vos leads qualifiés, vérifiés, RGPD compliant. Simplement.
                    </p>
                    <div className="mt-10 space-y-4">
                        {(
                            [
                                { Icon: MdPayments, label: 'Paiement sécurisé et rapide' },
                                { Icon: MdVerified, label: 'Acheteurs vérifiés' },
                                { Icon: MdInsights, label: 'Suivi de vos ventes en temps réel' },
                            ] as const
                        ).map(({ Icon, label }) => (
                            <div key={label} className="flex items-center gap-3">
                                <Icon size={18} className="text-leadrs-purple-focus/50" />
                                <span className="font-geist text-sm text-leadrs-purple-focus/80">
                                    {label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <p className="font-geist text-xs text-leadrs-purple-focus/30">
                    © {new Date().getFullYear()} Leadrs.co — Tous droits réservés.
                </p>
            </div>

            {/* Right — form */}
            <div className="col-span-12 overflow-y-auto bg-white lg:col-span-7">
                <div className="flex min-h-full w-full items-center justify-center px-6 py-12 lg:items-start lg:justify-start lg:px-16 lg:pt-[8vh]">
                    <div className="w-full max-w-xl">
                        <h1 className="font-crimson text-[42px] leading-[1.1] text-leadrs-text">
                            Rejoindre Leadrs
                        </h1>
                        <p className="mt-3 font-geist text-base text-leadrs-text-soft">
                            Vendez facilement vos leads.
                        </p>

                        {error && (
                            <div className="mt-4 rounded-lg bg-leadrs-red-soft px-4 py-3">
                                <p className="font-geist text-sm text-leadrs-red-focus">
                                    {error}
                                </p>
                            </div>
                        )}
                        {newUser.password !== newUser.confirmPassword && (
                            <div className="mt-4 rounded-lg bg-leadrs-red-soft px-4 py-3">
                                <p className="font-geist text-sm text-leadrs-red-focus">
                                    Les mots de passe ne correspondent pas
                                </p>
                            </div>
                        )}

                        <form className="mt-6" onSubmit={handleSubmit}>
                            <p className="mb-4 font-geist text-base font-medium text-leadrs-text">
                                Informations de votre activité
                            </p>
                            <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
                                <InputField
                                    variant="auth"
                                    extra=""
                                    label="Prénom*"
                                    placeholder="Jeff"
                                    id="name"
                                    type="text"
                                    onChange={handleChange}
                                />
                                <InputField
                                    variant="auth"
                                    extra=""
                                    label="Nom*"
                                    placeholder="Bezos"
                                    id="surname"
                                    type="text"
                                    onChange={handleChange}
                                />
                                <InputField
                                    variant="auth"
                                    extra=""
                                    label="Email*"
                                    placeholder="vous@exemple.com"
                                    id="email"
                                    type="text"
                                    onChange={handleChange}
                                />
                                <InputField
                                    variant="auth"
                                    extra=""
                                    label="Téléphone*"
                                    placeholder="+33 6 12 34 56 78"
                                    id="phone"
                                    type="text"
                                    onChange={handleChange}
                                />
                                <div>
                                    <InputField
                                        variant="auth"
                                        extra=""
                                        label="Mot de passe*"
                                        placeholder="********"
                                        id="password"
                                        type="password"
                                        onChange={handleChange}
                                    />
                                    <p className="ml-0.5 mt-1.5 font-geist text-xs text-leadrs-text-soft">
                                        Minimum 8 caractères
                                    </p>
                                </div>
                                <InputField
                                    variant="auth"
                                    extra=""
                                    label="Confirmer mot de passe*"
                                    placeholder="********"
                                    id="confirmPassword"
                                    type="password"
                                    onChange={handleChange}
                                />
                                <div className="col-span-1 md:col-span-2">
                                    <InputField
                                        variant="auth"
                                        extra=""
                                        label="Code de parrainage (facultatif)"
                                        placeholder="DF5a3e8RT"
                                        id="referal"
                                        type="text"
                                        value={newUser.referal}
                                        onChange={handleChange}
                                    />
                                    <p className="ml-0.5 mt-1.5 font-geist text-xs text-leadrs-yellow-focus">
                                        Si vous avez un code de parrainage, saisissez-le ici
                                    </p>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="mt-6 w-full rounded-lg bg-leadrs-brand py-3 font-geist text-base font-medium text-white transition-colors hover:bg-leadrs-brand-light"
                            >
                                S'inscrire
                            </button>
                        </form>

                        <div className="mt-5 flex items-center gap-3">
                            <div className="h-px w-full bg-leadrs-stroke" />
                            <span className="font-geist text-xs text-leadrs-text-soft">
                                OU
                            </span>
                            <div className="h-px w-full bg-leadrs-stroke" />
                        </div>

                        <div className="mt-4 flex w-full justify-center">
                            <GoogleLogin
                                onSuccess={(credentialResponse: any) => {
                                    loginGoogle(credentialResponse.credential);
                                }}
                                containerProps={{
                                    className: 'w-full',
                                }}
                                shape="circle"
                                size="large"
                                theme="outline"
                                logo_alignment="center"
                                onError={() => {
                                    console.log('Login Failed');
                                    setError('Échec de la connexion avec Google');
                                }}
                            />
                        </div>

                        <p className="mt-5 font-geist text-sm text-leadrs-text-soft">
                            Déjà inscrit ?{' '}
                            <a
                                href="/auth/sign-in/"
                                className="font-medium text-leadrs-purple-focus underline-offset-2 hover:underline"
                            >
                                Se connecter
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SignUpSeller;
